import React from 'react';

/**
 * Invoice Plan editor (Ryan, 15 Jul) — the client-invoicing counterpart to the
 * contractor-billing (AP) side. Set in Sales Ops on the SOW; frozen onto the
 * contract at build and consumed by the invoicing engine (trigger provisioning,
 * cadence scheduler, cap enforcement). Mirrors the backend InvoicePlan schema.
 *
 * Self-contained: reads sow.invoice_plan and writes back via updateSOW.
 */

const INVOICE_MODELS = [
  { value: 'hours_consumption', label: 'Hours of consumption (invoice per N hours used)' },
  { value: 'pct_completion', label: 'Percentage of completion (invoice per N%)' },
  { value: 'upfront', label: 'Upfront (invoice the whole block at once)' },
  { value: 'milestone', label: 'Milestone (invoice on named milestones)' },
];

const BOA_CADENCES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
];

// Empty string -> null so the backend treats a cleared field as unset.
const num = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));

export default function InvoicePlanFields({ sow = {}, updateSOW }) {
  const ct = sow.customer_contract_type || sow.contract_type || '';
  const naType = ct === 'fixed_fee' || ct === 'monthly';
  const showBoh = !naType && ct !== 'boa';
  const showBoa = !naType && ct !== 'boh';

  const plan = sow.invoice_plan || {};
  const setField = (field, value) => updateSOW('invoice_plan', { ...plan, [field]: value });

  const isIncrementModel =
    plan.model === 'hours_consumption' || plan.model === 'pct_completion';
  const incrementUnit = plan.model === 'pct_completion' ? '%' : 'hours';

  // Row helper called as a function (NOT a nested component) so inputs keep
  // focus across re-renders.
  const row = (label, control, hint) => (
    <div className="grid grid-cols-2" key={label}>
      <div className="px-4 py-3 bg-boss-surface border-r border-boss-border">
        <span className="text-boss-text-secondary text-sm font-medium">{label}</span>
        {hint && (
          <p className="text-boss-text-secondary/70 text-xs mt-1">{hint}</p>
        )}
      </div>
      <div className="px-4 py-3">{control}</div>
    </div>
  );

  const selectCls =
    'w-full bg-transparent text-boss-text text-sm focus:outline-none [&>option]:bg-boss-elevated';
  const inputCls = 'w-full bg-transparent text-boss-text text-sm focus:outline-none';

  return (
    <div className="bg-boss-base border border-boss-border rounded-lg overflow-hidden">
      <div className="bg-boss-elevated px-4 py-3 border-b border-boss-border">
        <h4 className="font-semibold text-boss-text text-sm">Invoice Plan</h4>
        <p className="text-boss-text-secondary text-xs mt-0.5">
          How the customer is invoiced — drives automated invoice generation.
        </p>
      </div>

      {naType && (
        <div className="px-4 py-4 text-boss-text-secondary text-sm">
          Invoice plan applies to Block of Hours and Bill-on-Actuals contracts.
        </div>
      )}

      {!naType && (
        <div className="divide-y divide-boss-border">
          {showBoh && (
            <>
              {row(
                'Invoice model',
                <select
                  value={plan.model || ''}
                  onChange={(e) => setField('model', e.target.value || null)}
                  className={selectCls}
                >
                  <option value="">Select model...</option>
                  {INVOICE_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>,
              )}

              {isIncrementModel &&
                row(
                  `Increment (${incrementUnit})`,
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={plan.increment_value ?? ''}
                    onChange={(e) => setField('increment_value', num(e.target.value))}
                    placeholder={plan.model === 'pct_completion' ? '25' : '100'}
                    className={inputCls}
                  />,
                  plan.model === 'pct_completion'
                    ? 'e.g. 25 for 25% increments'
                    : 'e.g. 100 for 100-hour blocks',
                )}

              {isIncrementModel &&
                row(
                  'Lead offset (hours)',
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={plan.lead_offset_hours ?? ''}
                    onChange={(e) => setField('lead_offset_hours', num(e.target.value))}
                    placeholder="10"
                    className={inputCls}
                  />,
                  'Generate each invoice this many hours early (90 of 100).',
                )}

              {row(
                'Upfront %',
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="any"
                  value={plan.upfront_pct ?? ''}
                  onChange={(e) => setField('upfront_pct', num(e.target.value))}
                  placeholder="e.g. 50"
                  className={inputCls}
                />,
                'Optional: invoice this % up front, then increments.',
              )}
            </>
          )}

          {showBoa && (
            <>
              {row(
                'BOA invoice cadence',
                <select
                  value={plan.boa_cadence || ''}
                  onChange={(e) => setField('boa_cadence', e.target.value || null)}
                  className={selectCls}
                >
                  <option value="">Select cadence...</option>
                  {BOA_CADENCES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>,
              )}

              {row(
                'Weekly cap (hours)',
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={plan.boa_weekly_cap_hours ?? ''}
                  onChange={(e) => setField('boa_weekly_cap_hours', num(e.target.value))}
                  placeholder="e.g. 20"
                  className={inputCls}
                />,
                "Optional: don't bill more than this per week.",
              )}

              {row(
                'Monthly cap (hours)',
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={plan.boa_monthly_cap_hours ?? ''}
                  onChange={(e) => setField('boa_monthly_cap_hours', num(e.target.value))}
                  placeholder="e.g. 100"
                  className={inputCls}
                />,
                "Optional: don't bill more than this per month.",
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
